import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchListings as requestListings } from '../../../utils/listingsTransport';
import { resolveListingImages } from '../../../utils/listingImage';
import { Alert } from '@arco-design/web-react';
import {
	AdvancedType,
	BasicType,
	getAncestorCustomListingIdx,
	getAncestorPinnedListing,
	getParentIdx,
	isBoundListingResolving,
	primeListings,
	resolveBoundListing,
	subscribeListings,
	t,
} from '@bay-email/core';
import { useEditorContext, useFocusIdx } from '@bay-email/editor';
import { useSelectedNode } from '@extensions/hooks/useSelectedNode';
import { useExtensionProps } from '@extensions/components/Providers/ExtensionProvider';
import { SingleListingPicker } from '../SingleListing/SingleListingPicker';
import {
	ATTACHED_LISTING_PICKER_EVENT,
	collectBoundDescendantIdxs,
} from './listingAttachment';
import { useInsideListingScope } from './useInsideListingScope';
import type { ListingSearchParams } from '../SingleListing/useListingSearch';
import { get } from 'lodash';

interface ListingItem {
	id: string;
	title?: string;
	address?: string;
	city?: string;
	stateProvince?: string;
	postalCode?: string;
	list_price?: number;
	beds?: number;
	baths?: number;
	sq_foot?: number;
	image_data?: string[];
	mls?: string;
	dateListed?: string;
	url?: string;
	status?: string;
	propertyType?: string;
	agentName?: string;
}

interface ActiveView {
	id: 'mylistings' | 'companylistings';
	page: number;
	size: number;
}

interface AttachedListingSectionProps {
	observedValues: Array<string | undefined | null>;
}

const ALL_PROPERTY_TYPE_VALUES = ['residential', 'multifamily', 'commercial', 'land'];

const SORT_FIELD_MAP: Record<string, string> = {
	list_price: 'list_price',
	adddatetime: 'adddatetime',
};

const normalizePriceFilterValue = (value?: string) => {
	if (!value) return '';
	return value.replace(/[^0-9]/g, '');
};

function isSectionType(type?: string | null) {
	return type === BasicType.SECTION || type === AdvancedType.SECTION;
}

function isColumnType(type?: string | null) {
	return type === BasicType.COLUMN || type === AdvancedType.COLUMN;
}

function hasListingSelection(block: any) {
	if (!block) return false;
	return Boolean(
		block?.data?.value?.selectedListingId || block?.data?.value?.selectedListing,
	);
}

function findAncestorSectionIdx(values: any, idx: string) {
	let parentIdx = getParentIdx(idx);

	while (parentIdx) {
		const parentBlock = get(values, parentIdx);
		if (isSectionType(parentBlock?.type)) return parentIdx;
		parentIdx = getParentIdx(parentIdx);
	}

	return null;
}

function collectDescendantColumnIdxs(block: any, idx: string, result: string[]) {
	if (!block) return;

	if (isColumnType(block.type)) {
		result.push(idx);
	}

	if (!Array.isArray(block.children)) return;

	block.children.forEach((child: any, childIndex: number) => {
		collectDescendantColumnIdxs(child, `${idx}.children.[${childIndex}]`, result);
	});
}

export function AttachedListingSection({ observedValues }: AttachedListingSectionProps) {
	const [listings, setListings] = useState<ListingItem[]>([]);
	const [total, setTotal] = useState<number>(0);
	const [loading, setLoading] = useState<boolean>(false);
	const [drawerOpened, setDrawerOpened] = useState(false);
	// First open only — see the note on handleDrawerOpen below.
	const hasOpenedRef = React.useRef(false);
	const handleDrawerOpen = useCallback(() => {
		if (hasOpenedRef.current) return;
		hasOpenedRef.current = true;
		setDrawerOpened(true);
		setLoading(true);
	}, []);
	const [selectedListing, setSelectedListing] = useState<ListingItem | null>(null);
	const [activeView, setActiveView] = useState<ActiveView>({
		id: 'mylistings',
		page: 1,
		size: 10,
	});
	const [openSignal, setOpenSignal] = useState(0);
	const { selectedNode } = useSelectedNode();
	const { focusIdx } = useFocusIdx();
	const {
		formHelpers,
		formState: { values },
	} = useEditorContext();
	const { isDesignAdmin } = useExtensionProps();
	const insideCustomListing = useInsideListingScope();
	const selectedNodeType = selectedNode?.type;
	const isSectionBlock = isSectionType(selectedNodeType);
	const isColumnBlock = isColumnType(selectedNodeType);

	// The scope that owns the listing for this block — the Listing Wrapper it
	// sits in, or the listing column. Null when this block owns its own.
	const ownerIdx = useMemo(
		() => getAncestorCustomListingIdx((values as any)?.content, focusIdx),
		[values, focusIdx],
	);
	const targetIdx = ownerIdx || focusIdx;

	const parentSectionIdx = useMemo(() => {
		if (!isColumnBlock) return null;
		return findAncestorSectionIdx(values, focusIdx);
	}, [focusIdx, isColumnBlock, values]);

	const parentSectionHasListing = useMemo(() => {
		if (!parentSectionIdx) return false;
		return hasListingSelection(get(values, parentSectionIdx));
	}, [parentSectionIdx, values]);

	const childColumnIdxs = useMemo(() => {
		if (!isSectionBlock) return [] as string[];

		const sectionBlock = get(values, focusIdx);
		const nextColumnIdxs: string[] = [];
		collectDescendantColumnIdxs(sectionBlock, focusIdx, nextColumnIdxs);
		return nextColumnIdxs;
	}, [focusIdx, isSectionBlock, values]);

	const hasChildColumnListing = useMemo(() => {
		if (!isSectionBlock) return false;
		return childColumnIdxs.some(idx => hasListingSelection(get(values, idx)));
	}, [childColumnIdxs, isSectionBlock, values]);

	// Resolve exactly the way the blocks render — an enclosing scope's listing
	// wins, and a block falls back to its own binding when the scope has none.
	// Reading only the owner (the old behaviour) made the panel disagree with
	// the canvas whenever a block inside an unbound wrapper or listing column
	// carried its own listing: the canvas painted the property, the panel
	// offered "Select Listing" for it, and no amount of waiting fixed it
	// because nothing was ever going to arrive at the address it was watching.
	//
	// Both halves go through the render cache, so a binding whose data has not
	// landed yet schedules its fetch here too and the subscription below
	// repaints when it does.
	const [listingResolving, setListingResolving] = useState(false);

	const syncFromBinding = useCallback(() => {
		const resolved =
			getAncestorPinnedListing((values as any)?.content, focusIdx) ||
			resolveBoundListing((get(values, focusIdx) as any)?.data?.value);

		setSelectedListing((resolved as ListingItem) || null);
		// A lookup runs for seconds. Without this the drawer spends all of it
		// saying "Select a listing" about a listing that is already selected.
		setListingResolving(
			isBoundListingResolving(
				(values as any)?.content,
				focusIdx,
				get(values, focusIdx) as any,
			),
		);
	}, [values, focusIdx]);

	useEffect(syncFromBinding, [syncFromBinding]);
	// The binding is an id resolved through a cache that fills asynchronously.
	// Without this the panel keeps offering "Select a listing" for a scope that
	// already has one, until an unrelated re-render happens to pick it up.
	useEffect(() => subscribeListings(syncFromBinding), [syncFromBinding]);

	useEffect(() => {
		if (typeof window === 'undefined') return;

		const handleOpen = (event: Event) => {
			const detail = (event as CustomEvent<{ idx?: string }>).detail;
			if (detail?.idx === focusIdx) {
				setOpenSignal(value => value + 1);
			}
		};

		window.addEventListener(ATTACHED_LISTING_PICKER_EVENT, handleOpen as EventListener);

		return () => {
			window.removeEventListener(
				ATTACHED_LISTING_PICKER_EVENT,
				handleOpen as EventListener,
			);
		};
	}, [focusIdx]);

	const getListings = useCallback(
		async (params: ListingSearchParams) => {
			setLoading(true);
			try {
				const { page, size, keyword, filters, sortBy, orderDirection } = params;
				const reqParams: Record<string, unknown> = {
					method: 'read',
					CacheData: true,
					Return_Query: false,
					Return_Array: true,
					startpage: page,
					MaxRows: size,
					IsMyTransactions: 'F',
					IsCoordinatorTransactions: 'F',
					ReturnFormat: 'json',
					propertytype: 'residential,multifamily,commercial,land',
					deleteflag: 'n',
					showallproperties: false,
					query: keyword?.trim() ? keyword : '*',
					searchfield: keyword?.trim() ? 'streetaddress,ml_number' : '',
					gridtabid: activeView.id,
				};

				// The drawer sends sort and filters on every page/search/sort change.
				// Dropping them here left sorting and filtering dead in the drawer for
				// every block that attaches its listing through this panel.
				if (sortBy && SORT_FIELD_MAP[sortBy]) {
					reqParams.orderby = SORT_FIELD_MAP[sortBy];
					reqParams.orderdirection = (orderDirection || 'DESC').toLowerCase();
				}

				if (filters) {
					reqParams.startdate = filters.startdate ?? '';
					reqParams.enddate = filters.enddate ?? '';
					reqParams.pricemin = normalizePriceFilterValue(filters.pricemin);
					reqParams.pricemax = normalizePriceFilterValue(filters.pricemax);
					reqParams.statuscode = Array.isArray(filters.status) ? filters.status.join(',') : '';

					if (Array.isArray(filters.propertytype)) {
						reqParams.propertytype = filters.propertytype.length
							? filters.propertytype.join(',')
							: ALL_PROPERTY_TYPE_VALUES.join(',');
					}
				}

				const responseData = await requestListings({ params: reqParams });
				let nextListings: ListingItem[] = [];

				if (responseData?.STATUS === 0 && Array.isArray(responseData?.QUERY)) {
					nextListings = responseData.QUERY.map((listing: Record<string, unknown>) => {
						const images = resolveListingImages(listing);

						return {
							// Keep every raw CFC field (ml_number, la_name, description, the
							// schools, …). onSelectListing primes this row as the block's
							// listing, so anything dropped here is a merge tag that can never
							// resolve — this panel used to keep 14 fields and lose the rest.
							...listing,
							id: `${listing.ml_number || ''}|${listing.mlsid || ''}|${listing.statuscode || ''}|${listing.reduceddatetime || ''}`,
							title: (listing.streetaddress as string) || undefined,
							address: (listing.streetaddress as string) || undefined,
							city: (listing.city as string) || undefined,
							stateProvince:
								(listing.stateProvince as string) ||
								(listing.state as string) ||
								undefined,
							postalCode:
								(listing.postalCode as string) || (listing.zip as string) || undefined,
							list_price:
								typeof listing.list_price === 'number'
									? listing.list_price
									: Number(listing.list_price || 0),
							mls: (listing.ml_number as string) || undefined,
							dateListed:
								(listing.listingDisplayDate as string) ||
								(listing.LISTINGDISPLAYDATE as string) ||
								(listing.AddDateTime as string) ||
								(listing.adddatetime as string) ||
								undefined,
							image_data: images,
							beds: Number(listing.beds || 0),
							baths: Number(listing.baths || 0),
							sq_foot: Number(listing.sq_foot || 0),
							url: (listing.url as string) || undefined,
							status: listing?.statuscode as string,
							propertyType: listing?.propertytype as string,
							agentName: (listing?.agentname as string) || '',
						};
					});
				}

				const totalCount = Number(responseData?.TOTALROWCOUNT || 0);
				setListings(nextListings);
				setTotal(totalCount);
				return { listings: nextListings, total: totalCount };
			} catch {
				setListings([]);
				setTotal(0);
				return { listings: [], total: 0 };
			} finally {
				setLoading(false);
			}
		},
		[activeView.id],
	);

	// The search grid is the drawer's content, not the panel's, so it loads when
	// the drawer opens rather than when the panel mounts. Mounting happens on
	// every focus change, which meant clicking any listing-capable block fired a
	// listings.cfc read for a drawer most clicks never open — including blocks
	// whose listing was already resolved and sitting in the cache.
	// Results already loaded for a view, kept for the life of this panel.
	//
	// Switching My Listings <-> Company Listings changes activeView, which
	// rebuilds getListings and re-ran the effect below — so flipping the tab back
	// and forth refetched a list the drawer had already shown. The drawer is
	// open and the data has not changed; serve it from here instead.
	//
	// Only the default page-1, no-keyword, no-filter load is cached, which is
	// exactly what a view switch asks for: the drawer's handleViewChange resets
	// search, filters, sort and page whenever the tab changes. Paging, searching
	// and filtering still go straight to getListings and always hit the network.
	const viewResultsRef = React.useRef(
		new Map<string, { listings: ListingItem[]; total: number }>(),
	);

	useEffect(() => {
		if (!drawerOpened) return;

		const viewId = activeView.id;
		const cached = viewResultsRef.current.get(viewId);

		if (cached) {
			setListings(cached.listings);
			setTotal(cached.total);
			setLoading(false);
			return;
		}

		void getListings({ ...activeView, keyword: '' }).then(result => {
			if (result) viewResultsRef.current.set(viewId, result);
		});
	}, [drawerOpened, activeView, getListings]);

	const onSelectListing = useCallback(
		(listing: ListingItem | null) => {
			setSelectedListing(listing);
			// Persist the binding only; seed the render cache with the values we
			// already hold so the canvas paints without waiting on a lookup.
			if (listing) primeListings([listing as any]);

			// A listing belongs to the scope that owns it, not to whichever block
			// happened to be focused. Picking from a dynamic image inside a wrapper
			// or listing column writes to that owner, so every sibling in the scope
			// takes the listing instead of just the one block.
			formHelpers.change(`${targetIdx}.data.value.selectedListingId`, listing?.id || '');
			// Clear any resolved copy a pre-migration save left on the block.
			formHelpers.change(`${targetIdx}.data.value.selectedListing`, null);

			// Descendants inherit from the owner, so drop bindings of their own —
			// theirs would win. Runs on clear too, or a cleared scope keeps showing
			// the listing it just dropped.
			const boundDescendantIdxs: string[] = [];
			collectBoundDescendantIdxs(get(values, targetIdx), targetIdx, boundDescendantIdxs);
			boundDescendantIdxs.forEach(idx => {
				formHelpers.change(`${idx}.data.value.selectedListingId`, '');
				formHelpers.change(`${idx}.data.value.selectedListing`, null);
			});

			// A column and the section above it can't both own the listing. Only
			// the ascent needs handling here — the walk above covers descendants.
			if (!ownerIdx && isColumnBlock && parentSectionIdx) {
				formHelpers.change(`${parentSectionIdx}.data.value.selectedListingId`, '');
				formHelpers.change(`${parentSectionIdx}.data.value.selectedListing`, null);
			}
		},
		[
			formHelpers,
			isColumnBlock,
			ownerIdx,
			parentSectionIdx,
			targetIdx,
			values,
		],
	);

	const onPaginationChange = useCallback(
		(params: ListingSearchParams) => {
			void getListings(params);
		},
		[getListings],
	);

	// The two "select a listing" notices that used to live here said the same
	// thing as the picker's own empty state one element below, so all three
	// stacked on an unselected block. The picker's is the one that survived.
	//
	// ponytail: that leaves `observedValues` unused — it existed only to decide
	// whether this block's content mentioned a listing merge tag. Drop the prop
	// and its six call sites (AdvancedTable, Button, Column, Image, Table, Text)
	// at ship; kept for now to hold the diff to the banners.

	// Containers only. A section or column sitting inside a Custom Listing
	// container has nothing to attach — the container owns the single listing
	// and cascades it down, so a second container-level picker would just
	// contradict it. The Column panel gates its Collapse.Item on the same hook,
	// or it renders a "Listing Settings" header around nothing.
	//
	// Content blocks (text, image, button, table) keep their picker even inside
	// a scope: the block's own selection is checked before the ancestor's when
	// the listing resolves, so a block that opts out of the container's listing
	// still renders correctly.
	if (insideCustomListing && (isSectionBlock || isColumnBlock)) return null;

	return (
		<div className='p-3'>
			{isColumnBlock && parentSectionHasListing && (
				<Alert
					type='warning'
					style={{ marginBottom: 16 }}
					content={t(
						'This section already has a listing selected. Selecting one for this column will clear the section listing.',
					)}
				/>
			)}
			{isSectionBlock && hasChildColumnListing && (
				<Alert
					type='warning'
					style={{ marginBottom: 16 }}
					content={t(
						'One or more columns already have a listing selected. Selecting one for this section will clear those column listings.',
					)}
				/>
			)}
			<div style={{ margin: '-12px -12px 0' }}>
				<SingleListingPicker
					listings={listings}
					selectedListing={selectedListing}
					total={total}
					size={10}
					loading={loading}
					onSelect={onSelectListing}
					onPaginationChange={onPaginationChange}
					disabled={isDesignAdmin}
					resolving={listingResolving}
					onOpen={handleDrawerOpen}
					disabledTooltip={
						insideCustomListing
							? t(
									"Users choosing this template will be able to select one listing to instantly populate all listing dynamic fields across this block's parent listing wrapper or column.",
								)
							: // No wrapper or column above it, so there is no parent scope to
								// name — this block's listing fills this block alone.
								t(
									'Users choosing this template will be able to select one listing to instantly populate all listing dynamic fields inside this block.',
								)
					}
					setActiveView={setActiveView}
					openSignal={openSignal}
				/>
			</div>
		</div>
	);
}
